import type { RemovalTombstone, UnsyncedDraft, UnsyncedPhoto } from '../../domain/drafts';
import { isDomainError } from '../../domain/errors';
import type { Listing, Photo } from '../../domain/listings';
import { Backoff } from './backoff';
import type { DraftOutbox } from './draftOutbox';
import type { DraftSyncTransport } from './DraftSyncTransport';
import { classifySyncFailure, type SyncStep } from './syncFailure';

export type DraftSyncEngineOptions = {
  outbox: DraftOutbox;
  transport: DraftSyncTransport;
  isOnline?: () => boolean;
  /**
   * Hear about any answer the page gets from the service. A connection can
   * come back without the browser ever having said it was gone (no `online`
   * event), so another screen's answer is the first sign the wait is over.
   */
  hearsFromService?: (listener: () => void) => () => void;
  /** For backoff jitter; tests pin it. */
  random?: () => number;
  /** A draft reached the service as the device has it; called just before it leaves the outbox. */
  onSynced?: (listing: Listing) => void;
};

/** What a draft needs next to match the device, worked out from its row alone. */
type Step =
  | { type: 'upload'; photo: UnsyncedPhoto }
  | { type: 'create' }
  | { type: 'patch' }
  | { type: 'deletePhoto'; photoId: string }
  | { type: 'addPhotos'; photoIds: string[] }
  | { type: 'reorder' }
  | { type: 'done' };

/** After a step: keep working on this draft, move to the next, or end the pass. */
type Outcome = 'continue' | 'next' | 'stop';

/**
 * Syncs the outbox to the service (research R3, R5, R6). One engine per page,
 * and it is single-flight: one pass at a time, and any trigger during a pass
 * (`kick`, a local change, the connection coming back) asks for exactly one
 * more. A pass first finishes removals (deleting whatever reached the
 * service), then takes drafts oldest first, one at a time, so at most one
 * draft's uploads are pending on the service.
 *
 * Every step is chosen afresh from the draft's current row and every write is
 * idempotent by an id the device chose, so a retry, a lost answer or an edit
 * landing mid-request can only repeat a step, never double one (NFR-002).
 * A draft is uploaded photo by photo, then created once with all of them; an
 * edit saved after the create was sent is applied through the editor's
 * calls, and the draft leaves the outbox once nothing differs (FR-008).
 */
export class DraftSyncEngine {
  private readonly outbox: DraftOutbox;
  private readonly transport: DraftSyncTransport;
  private readonly isOnline: () => boolean;
  private readonly hearsFromService: (listener: () => void) => () => void;
  private readonly random: () => number;
  private readonly onSynced: (listing: Listing) => void;

  private started = false;
  /** Bumped by start and stop; a pass from an older generation writes nothing. */
  private generation = 0;
  private controller = new AbortController();
  private stopListening: (() => void) | null = null;
  private stopHearingService: (() => void) | null = null;
  private running: Promise<void> | null = null;
  private readonly inFlight = new Set<Promise<void>>();
  private again = false;
  private passes = 0;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private timerDue = 0;
  private readonly backoff: Backoff;
  /** Waiting out a backoff because the service couldn't be reached. */
  private unreachable = false;
  /** Drafts backed off on their own (an unexpected answer), and when they may try again. */
  private readonly draftRetryAt = new Map<string, number>();
  private readonly draftBackoffs = new Map<string, Backoff>();
  /** The revision carried by a draft's first create; a repeat answers with that one's content. */
  private readonly createRevision = new Map<string, number>();
  /** Drafts whose uploads were just redone after an expiry, so a second refusal backs off. */
  private readonly repaired = new Set<string>();
  /** The service's latest answer for each confirmed draft. */
  private readonly latest = new Map<string, Listing>();
  /** Drafts this page has sent anything for; removing any other needs no call. */
  private readonly touched = new Set<string>();

  constructor({
    outbox,
    transport,
    isOnline = () => navigator.onLine,
    hearsFromService = () => () => {},
    random = Math.random,
    onSynced = () => {},
  }: DraftSyncEngineOptions) {
    this.outbox = outbox;
    this.transport = transport;
    this.isOnline = isOnline;
    this.hearsFromService = hearsFromService;
    this.random = random;
    this.onSynced = onSynced;
    this.backoff = new Backoff(random);
  }

  /** How many passes have started; for tests of single flight. */
  get passCount(): number {
    return this.passes;
  }

  // ── Triggers ──────────────────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;
    this.generation += 1;
    this.controller = new AbortController();
    this.stopListening = this.outbox.subscribeLocalChanges(() => this.kick());
    this.stopHearingService = this.hearsFromService(() => {
      if (this.unreachable && !this.running) this.connectivityChanged();
    });
    this.kick();
  }

  /** Abort the request in flight and write nothing more (sign-out, R10). */
  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.generation += 1;
    this.controller.abort();
    this.stopListening?.();
    this.stopListening = null;
    this.stopHearingService?.();
    this.stopHearingService = null;
    this.unreachable = false;
    this.clearTimer();
    this.running = null;
    this.again = false;
  }

  /** Any reason to look again: page visible, a verification, a local change, a timer. */
  kick(): void {
    if (!this.started) return;
    if (this.running) {
      this.again = true;
      return;
    }
    const generation = this.generation;
    const run = (async () => {
      const start = performance.now();
      const hadWork = this.outbox.drafts().length + this.outbox.tombstones().length > 0;
      do {
        this.again = false;
        this.passes += 1;
        await this.pass(generation);
      } while (this.again && this.current(generation));
      // NFR-001: how long a run took to drain the outbox (DevTools → Performance).
      if (hadWork) performance.measure('bulk.sync', { start, end: performance.now() });
    })().finally(() => {
      this.inFlight.delete(run);
      if (this.running === run) this.running = null;
      // A trigger after the last pass looked at the outbox would otherwise be lost.
      if (this.again && this.current(generation)) this.kick();
    });
    this.running = run;
    this.inFlight.add(run);
  }

  /** The connection changed: forget the backoff and try now (R5). */
  connectivityChanged(): void {
    if (!this.started) return;
    this.unreachable = false;
    this.backoff.reset();
    this.clearTimer();
    this.kick();
  }

  /** Resolves once no pass is running (a backoff timer may still be set). */
  async whenIdle(): Promise<void> {
    while (this.inFlight.size > 0) await Promise.all([...this.inFlight]);
  }

  // ── A pass ────────────────────────────────────────────────────────────

  private current(generation: number): boolean {
    return this.started && this.generation === generation;
  }

  private async pass(generation: number): Promise<void> {
    // This pass makes any retry a timer was waiting for; a failure sets a new one.
    this.clearTimer();
    const waiting = (d: UnsyncedDraft) => d.nextAttemptAt !== null && !this.draftRetryAt.has(d.id);
    if (this.outbox.drafts().some(waiting)) {
      this.outbox.recordAll((d) => (waiting(d) ? { nextAttemptAt: null } : {}));
    }
    try {
      while (this.current(generation)) {
        if (!this.isOnline()) return;
        const tombstone = this.outbox.tombstones()[0];
        if (tombstone) {
          if ((await this.removeFromService(generation, tombstone)) === 'stop') return;
          continue;
        }
        const draft = this.nextDraft();
        if (!draft) return;
        if ((await this.syncDraft(generation, draft.id)) === 'stop') return;
      }
    } finally {
      if (this.current(generation)) this.wakeForDraftRetries();
    }
  }

  /**
   * Oldest first, skipping drafts that wait for the person or for their own
   * backoff. A batch made together goes from its last item to its first, so
   * the service's newest-first list shows it in the order it was reviewed.
   */
  private nextDraft(): UnsyncedDraft | undefined {
    const now = Date.now();
    return inSyncOrder(this.outbox.drafts()).find((d) => {
      if (d.blocker === 'chooseCollection' || d.photos.some((p) => p.failure !== null))
        return false;
      const due = this.draftRetryAt.get(d.id);
      // A save clears `nextAttemptAt`, so an edited draft goes again at once.
      return due === undefined || due <= now || d.nextAttemptAt === null;
    });
  }

  private async syncDraft(generation: number, id: string): Promise<Outcome> {
    for (;;) {
      const draft = this.outbox.get(id);
      // Removed while a request was out: its tombstone takes over.
      if (!draft) return 'next';
      const step = nextStep(draft);
      if (step.type === 'done') {
        this.synced(draft);
        return 'next';
      }
      const outcome = await this.run(generation, draft, step);
      if (outcome !== 'continue') return outcome;
    }
  }

  private async run(
    generation: number,
    draft: UnsyncedDraft,
    step: Exclude<Step, { type: 'done' }>,
  ): Promise<Outcome> {
    const id = draft.id;
    this.touched.add(id);
    switch (step.type) {
      case 'upload': {
        const { photo } = step;
        return this.call(
          generation,
          'upload',
          id,
          photo.id,
          (signal) => this.transport.uploadPhoto(photo.id, photo.file, signal),
          () => this.outbox.recordPhoto(id, photo.id, { uploaded: true }),
        );
      }
      case 'create': {
        const revision = this.createRevision.get(id) ?? draft.localRevision;
        this.createRevision.set(id, revision);
        const photoIds = draft.photos.map((p) => p.id);
        return this.call(
          generation,
          'create',
          id,
          null,
          (signal) => this.transport.createDraft(id, draft.fields, photoIds, signal),
          (listing) => {
            this.latest.set(id, listing);
            this.createRevision.delete(id);
            this.repaired.delete(id);
            this.outbox.record(id, {
              serverConfirmed: true,
              sentRevision: revision,
              serverPhotoIds: listing.photos.map((p) => p.id),
            });
          },
        );
      }
      case 'patch': {
        const revision = draft.localRevision;
        return this.call(
          generation,
          'patch',
          id,
          null,
          (signal) => this.transport.patchContent(id, draft.fields, signal),
          (listing) => {
            this.latest.set(id, listing);
            this.outbox.record(id, {
              sentRevision: revision,
              serverPhotoIds: listing.photos.map((p) => p.id),
            });
          },
        );
      }
      case 'deletePhoto': {
        const { photoId } = step;
        return this.call(
          generation,
          'deletePhoto',
          id,
          null,
          (signal) => this.transport.deletePhoto(id, photoId, signal),
          () =>
            this.photosNow(
              id,
              draft.serverPhotoIds.filter((p) => p !== photoId),
            ),
        );
      }
      case 'addPhotos':
        return this.call(
          generation,
          'addPhotos',
          id,
          null,
          (signal) => this.transport.addPhotos(id, step.photoIds, signal),
          (photos) => {
            this.repaired.delete(id);
            this.photosNow(id, photos);
          },
        );
      case 'reorder': {
        const order = draft.photos.map((p) => p.id);
        return this.call(
          generation,
          'reorder',
          id,
          null,
          (signal) => this.transport.reorderPhotos(id, order, signal),
          (photos) => this.photosNow(id, photos),
        );
      }
    }
  }

  /** Record the photos the service now holds, as an answer or as ids still kept. */
  private photosNow(id: string, photos: Photo[] | string[]): void {
    const ids = photos.map((p) => (typeof p === 'string' ? p : p.id));
    const listing = this.latest.get(id);
    if (listing) {
      const known = new Map(listing.photos.map((p) => [p.id, p]));
      for (const p of photos) if (typeof p !== 'string') known.set(p.id, p);
      const ordered = ids.flatMap((photoId) => known.get(photoId) ?? []);
      this.latest.set(id, {
        ...listing,
        photos: ordered.map((p, position) => ({ ...p, position })),
      });
    }
    this.outbox.record(id, { serverPhotoIds: ids });
  }

  private synced(draft: UnsyncedDraft): void {
    const listing = this.latest.get(draft.id);
    this.forget(draft.id);
    if (listing) this.onSynced(listing);
    this.outbox.settle(draft.id);
  }

  private forget(id: string): void {
    this.latest.delete(id);
    this.createRevision.delete(id);
    this.repaired.delete(id);
    this.draftRetryAt.delete(id);
    this.draftBackoffs.delete(id);
  }

  /** Delete whatever of a removed draft reached the service (FR-009c); 404s count as done. */
  private async removeFromService(
    generation: number,
    tombstone: RemovalTombstone,
  ): Promise<Outcome> {
    const { draftId, photoIds } = tombstone;
    this.forget(draftId);
    if (this.touched.has(draftId)) {
      const listingGone = await this.call(
        generation,
        'deleteListing',
        null,
        null,
        (signal) => this.transport.deleteListing(draftId, signal),
        () => {},
      );
      if (listingGone !== 'continue') return 'stop';
      for (const photoId of photoIds) {
        const imageGone = await this.call(
          generation,
          'deletePendingImage',
          null,
          null,
          (signal) => this.transport.deletePendingImage(photoId, signal),
          () => {},
        );
        if (imageGone !== 'continue') return 'stop';
      }
    }
    this.touched.delete(draftId);
    this.outbox.dropTombstone(draftId);
    return 'next';
  }

  // ── One call and its answer ───────────────────────────────────────────

  private async call<T>(
    generation: number,
    step: SyncStep,
    draftId: string | null,
    photoId: string | null,
    send: (signal: AbortSignal) => Promise<T>,
    onAnswer: (value: T) => void,
  ): Promise<Outcome> {
    let value: T;
    try {
      value = await send(this.controller.signal);
    } catch (error) {
      if (!this.current(generation)) return 'stop';
      return this.failed(error, step, draftId, photoId);
    }
    if (!this.current(generation)) return 'stop';
    this.answered(draftId);
    onAnswer(value);
    return 'continue';
  }

  /** Any answer means the connection and session work again. */
  private answered(draftId: string | null): void {
    this.unreachable = false;
    this.backoff.reset();
    if (draftId) this.draftBackoffs.get(draftId)?.reset();
    if (this.outbox.drafts().some((d) => isPersonBlocker(d.blocker))) {
      this.outbox.recordAll((d) => (isPersonBlocker(d.blocker) ? { blocker: null } : {}));
    }
  }

  private failed(
    error: unknown,
    step: SyncStep,
    draftId: string | null,
    photoId: string | null,
  ): Outcome {
    const failure = classifySyncFailure(error, step);
    const removing = step === 'deleteListing' || step === 'deletePendingImage';
    if (removing && failure.kind !== 'needsPerson' && failure.kind !== 'transient') {
      // A refusal to delete means there's nothing of mine there to delete.
      return 'continue';
    }
    switch (failure.kind) {
      case 'needsPerson': {
        const blocker = failure.need === 'signIn' ? 'needsSignIn' : 'needsVerification';
        // Paused until a trigger finds it resolved: no timer.
        this.outbox.recordAll((d) => (d.blocker === 'chooseCollection' ? {} : { blocker }));
        return 'stop';
      }
      case 'permanentPhoto':
        if (draftId && photoId)
          this.outbox.recordPhoto(draftId, photoId, { failure: failure.reason });
        return 'next';
      case 'permanentCollection':
        if (draftId) this.outbox.record(draftId, { blocker: 'chooseCollection' });
        return 'next';
      case 'gone':
        // Deleted elsewhere after it synced: removal wins, nothing to delete.
        if (draftId) {
          this.forget(draftId);
          this.outbox.settle(draftId);
        }
        return 'next';
      case 'repairUploads':
        if (draftId && !this.repaired.has(draftId)) {
          this.repaired.add(draftId);
          const draft = this.outbox.get(draftId);
          for (const p of draft?.photos ?? []) {
            if (!draft?.serverPhotoIds.includes(p.id))
              this.outbox.recordPhoto(draftId, p.id, { uploaded: false });
          }
          return 'continue';
        }
        // Refused again right after a re-upload: unexpected, so back off this draft.
        if (draftId) this.repaired.delete(draftId);
        return this.backOffDraft(draftId);
      case 'transient':
        if (failure.scope === 'draft') return this.backOffDraft(draftId);
        // Asked to slow down is an answer: another screen's answer doesn't end that wait.
        return this.backOffEverything(!isDomainError(error, 'throttled'));
    }
  }

  /** `unreachable`: the service didn't answer, rather than answered something unexpected. */
  private backOffEverything(unreachable = true): Outcome {
    const delay = this.backoff.next();
    const due = Date.now() + delay;
    this.unreachable = unreachable;
    this.outbox.recordAll(() => ({ nextAttemptAt: due }));
    this.wakeIn(delay);
    return 'stop';
  }

  private backOffDraft(draftId: string | null): Outcome {
    // Removals have no draft to back off; they ride the engine's backoff.
    if (!draftId) return this.backOffEverything(false);
    let backoff = this.draftBackoffs.get(draftId);
    if (!backoff) {
      backoff = new Backoff(this.random);
      this.draftBackoffs.set(draftId, backoff);
    }
    const due = Date.now() + backoff.next();
    this.draftRetryAt.set(draftId, due);
    this.outbox.record(draftId, { nextAttemptAt: due });
    return 'next';
  }

  // ── Timers ────────────────────────────────────────────────────────────

  private wakeForDraftRetries(): void {
    const now = Date.now();
    for (const [id, due] of this.draftRetryAt) {
      if (!this.outbox.get(id)) this.draftRetryAt.delete(id);
      else this.wakeIn(Math.max(0, due - now));
    }
  }

  /** Kick after `ms`, unless an earlier wake-up is already set. */
  private wakeIn(ms: number): void {
    const due = Date.now() + ms;
    if (this.timer && this.timerDue <= due) return;
    this.clearTimer();
    this.timerDue = due;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.kick();
    }, ms);
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/** Oldest batch first; within a batch (one `createdAt`), last item first. */
function inSyncOrder(drafts: readonly UnsyncedDraft[]): UnsyncedDraft[] {
  const batches: UnsyncedDraft[][] = [];
  for (const draft of drafts) {
    const batch = batches.find((b) => b[0].createdAt === draft.createdAt);
    if (batch) batch.push(draft);
    else batches.push([draft]);
  }
  return batches
    .sort((a, b) => a[0].createdAt - b[0].createdAt)
    .flatMap((batch) => batch.reverse());
}

function isPersonBlocker(blocker: UnsyncedDraft['blocker']): boolean {
  return blocker === 'needsSignIn' || blocker === 'needsVerification';
}

/**
 * The next thing a draft needs, in order: its photos uploaded, the one
 * create, then (for edits saved after the create was sent) content, removed
 * photos, added photos and order — until the service matches the device.
 */
function nextStep(draft: UnsyncedDraft): Step {
  const toUpload = draft.photos.find((p) => !p.uploaded && p.failure === null);
  if (toUpload) return { type: 'upload', photo: toUpload };
  if (!draft.serverConfirmed) return { type: 'create' };
  if ((draft.sentRevision ?? 0) < draft.localRevision) return { type: 'patch' };

  const local = draft.photos.map((p) => p.id);
  const server = draft.serverPhotoIds;
  const extra = server.find((id) => !local.includes(id));
  if (extra) return { type: 'deletePhoto', photoId: extra };
  const missing = local.filter((id) => !server.includes(id));
  if (missing.length > 0) return { type: 'addPhotos', photoIds: missing };
  if (local.some((id, i) => server[i] !== id)) return { type: 'reorder' };
  return { type: 'done' };
}
